import { FIELD_TYPE } from '../../constants';
import { getImageUrlFromValue } from '../../services/utilities';

// functions for creating groups to show in resultatentabel form selections

/**
 * Create an object with all the fields to display in the resultatentabel and detailpanel
 * @param {Array} features a list of ol/features (from store/selections[n]/results[n]/features)
 * @param {Object} layer the layerObject (from store/layers[n])
 */

export const objectData = (features, layer, isMobile, hasBrk, locale) => {
    const attibutes = getAttributes(layer);
    return features
        .map((feature) => ({ feature, data: feature.getProperties() }))
        .map(({ feature, data }, index) => {
            const info = attibutes.reduce((accumulator, column) => {
                const value = data[column.attribute];
                const dataWithValue = { ...column, value };
                const hyperlinkText = column.hyperlinkText;
                accumulator[column.attribute] = { ...dataWithValue, displayValue: getValue(dataWithValue, locale), media: getMedia(dataWithValue, isMobile) };
                accumulator[column.attribute].hyperlinkText = hyperlinkText && value ? getReplacedAttributeValue(hyperlinkText, value) : '';
                return accumulator;
            }, {});

            // provide a unique ID column to avoid conflicts with the ID column in the data
            info.nedGlobeRowId = index;

            if (hasBrk) {
                const localIdentification = info?.identificatieLokaalID?.value;
                if (localIdentification) {
                    // TODO next line contains a fix for the inconsistancy of the casing in akrKadastraleGemeenteCodeWaarde
                    // Either the supplier changes this or we need to consider a way to map these inconsistancies to the correct values
                    const AKRKadastraleGemeenteCodeWaarde = info?.AKRKadastraleGemeenteCodeWaarde?.value || data.akrKadastraleGemeenteCodeWaarde;
                    const sectie = info?.sectie?.value;
                    const perceelnummer = info?.perceelnummer?.value;
                    const hasParcelData = AKRKadastraleGemeenteCodeWaarde && sectie && perceelnummer > 0;
                    if (hasParcelData) {
                        const title = `(${AKRKadastraleGemeenteCodeWaarde}) ${sectie} ${perceelnummer}`;
                        info.parcel = {
                            parcelIdentificationLocalID: info.identificatieLokaalID.value,
                            title: title
                        };
                    }
                }
            }
            return {
                // if a feature has no id_ (like imported feature) we use the ol_uid (from openlayers)
                id: feature.id_ || feature.ol_uid,
                feature,
                data,
                info
            };
        });
};

export const sortFeatures = (layer, features) => {
    const isFeedBack = layer.type === 'feedback';
    return isFeedBack ? [...features].sort(sortFeedBackFeatures) : features;
};

/**
 * Return an array with all the columns to display in the resultatentabel and detailpanel
 * @param {Object} layer the layerObject (from store/layers[n])
 */
export const getAttributes = (layer) => {
    const isFeedBack = layer.type === 'feedback';
    if (isFeedBack) {
        const feedbackFields = [...layer.extended.feedbackFields]
            .sort((a, b) => {
                if (a.order > b.order) {
                    return 1;
                }
                if (a.order < b.order) {
                    return -1;
                }
                return 0;
            });
        return feedbackFields.map(attribute => ({ ...layer.extended.informationAttributes.find((themeAttribute) => themeAttribute.attribute === attribute.name), ...attribute }));
    }
    return layer.extended.informationAttributes;
};

/**
 * Return an array with all the columns to display in the resultatentabel and detailpanel from featureProperties
 * @param {Object} layer the layerObject (from store/layers[n])
 */
export const getFeatureProperties = (features) => {
    const allproperties = [...new Set(
        features
            .flatMap(feature => Object.keys(feature.getProperties()))
    )];
    return allproperties
        .filter(prop => prop !== 'geometry')
        .map((prop, index) => (getInformationAttributes(prop, index)));
};

export const getInformationAttributes = (prop, index) => {
    return {
        'attribute': prop,
        'alias': prop,
        'order': index,
        'displayInTable': true,
        'displayInDetail': true,
        'harvest': false,
        'popup': true,
        'hyperlink': false,
        'hyperlinkText': null,
        'hyperlinkExpression': null,
        'hyperlinkImage': false,
        'openExtern': false,
        'dataType': null,
        'exportable': true,
        'print': true,
        'unavailable': false,
        'errorMessage': ''
    };
};

const sortFeedBackFeatures = (a, b) => {
    const aanmaakdatumA = a.getProperties().aanmaakdatum;
    const aanmaakdatumB = b.getProperties().aanmaakdatum;
    if (aanmaakdatumA) {
        const dateA = new Date(aanmaakdatumA).getTime();
        const dateB = new Date(aanmaakdatumB).getTime();
        if (dateA > dateB) {
            return -1;
        }
        if (dateA < dateB) {
            return 1;
        }
    }
    return 0;
};

export const getValue = (item, locale) => {
    if (item.dataType === 'xsd:dateTime' || item.dataType === 'xsd:date') {
        return getDateValue(item, locale);
    }
    if (item.domain) {
        const keyValue = item.domain.keyvalues.find(({ key }) => key === item.value);
        if (keyValue) {
            return keyValue.value;
        }
    }
    if (item.hyperlinkExpression && item.hyperlink) {
        return item.value ? getReplacedAttributeValue(item.hyperlinkExpression, item.value) : '';
    }
    return item.value;
};

export const getReplacedAttributeValue = (item, replaceval) => {
    return item.replace(/#(attribute|attribuut)#/gi, replaceval);
};

const getDateValue = (item, locale) => {
    // ESRI data can contain "null" strings.
    if (item.value === 'null') {
        return '';
    }

    // If date does not start with a year, return the item value
    if (!validDate(item.value)) {
        return item.value;
    }

    if (item.dataType === 'xsd:dateTime') {
        return toDateTime(item.value, locale);
    }

    if (item.dataType === 'xsd:date') {
        return toDate(item.value, locale);
    }

    return item.value ?? '';
};

function toDateTime(value, locale) {
    try {
        const dateString = value?.match('.+?(?=[Z])')[0];
        return dateString && new Date(dateString).toLocaleString(locale);

    } catch {
        let result = value;

        const dateString = value;
        result = dateString && new Date(dateString).toLocaleString(locale);

        //  toLocalDateString (and maybe also toLocaleString) does not raise an exception
        //  but returns the value 'Invalid Date'
        //  in this case return the original value
        if (result === 'Invalid Date') {
            return value;
        } else {
            return result ?? '';
        }
    }
}

function toDate(value, locale) {
    try {
        const dateString = value?.match('.+?(?=[Z])')[0];
        return dateString && new Date(dateString).toLocaleDateString(locale);

    } catch {

        let result = value;
        const dateString = value;
        result = dateString && new Date(dateString).toLocaleDateString(locale);

        //  toLocalDateString (and maybe also toLocaleString) does not raise an exception
        //  but returns the value 'Invalid Date'
        //  in this case return the original value
        if (result === 'Invalid Date') {
            return value;
        } else {
            return result ?? '';
        }
    }
}



function validDate(value) {
    if (value) {
        // we can only parse a date if it starts with a year.  yyyy-mm-dd or yyyy/mm/dd
        let parts = value.split('-');
        if (parts.length === 0) {
            parts = value.split('/');
        }

        return parts.length > 0 && parts[0].length === 4;
    } else {
        return true;
    }

}


const getMedia = (item, isMobile) => {
    const isPhoto = item.type === FIELD_TYPE.FOTO;
    const link = item.href ? {
        href: item.href,
        src: item.src,
        extern: item.openExtern || isMobile
    } : undefined;
    const photo = () => {
        if (item.value && isPhoto) {
            return { src: getImageUrlFromValue(item.value) };
        }
        return undefined;
    };
    return { isPhoto, photo: photo(), link };
};

export const getGroups = (selectionData, isMobile, hasBrk, locale) => {
    if (!selectionData) {
        return [];
    }
    const results = selectionData.results || [];
    return results
        .filter(({ features }) => features?.length > 0)
        .map(({ features, layer, meta, limitedResults }) => {
            let editable = false;
            let remove = false;
            if (layer.type === 'feedback') {
                editable = layer.extended.userRights.edit || false;
                remove = layer.extended.userRights.remove || false;
            }
            if (layer.type === 'theme') {
                editable = layer.data.isTransactional && layer.data.allowWfsTUpdate || false;
                remove = false;
            }
            const exportable = layer.extended.exportable;
            const selectable = editable || exportable;
            const sortedFeatures = sortFeatures(layer, features);
            return {
                title: layer.data.title,
                id: layer.id,
                features: sortedFeatures,
                objects: objectData(sortedFeatures || [], layer, isMobile, hasBrk, locale),
                exportable,
                download: true,
                editable,
                remove,
                selectable,
                printable: layer.extended.print,
                informationAttributes: getAttributes(layer),
                tabClose: false,
                count: features?.length || 0,
                layer,
                meta,
                limitedResults,
                order: meta?.theme?.order || meta?.feedback?.order,
                type: selectionData.type
            };
        })
        .filter(({ limitedResults }) => limitedResults !== true);
};

export const getLimitedResultsGroups = (selectionData) => {
    if (!selectionData) {
        return [];
    }
    const results = selectionData.results || [];
    return results
        .map(({ layer, limitedResults }) => {
            return {
                title: layer.data.title,
                limitedResults
            };
        })
        .filter(({ limitedResults }) => limitedResults === true)
        .map(({ title }) => title);
};
